import {BaseElementAsync, MsgOver, OSkinableInit} from "/@back@/commons/basis.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IView, IViewApi, IViewsContainer, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspUiEnv, WSP, Wsp} from "/@lib@/wsp/wsp.js";
import {IInfoBroker, InfoBrokerBasic} from "/@lib@/commons/infos.js";
import {ESrcField, ESrcSt, JSrcFields, JSrcFieldsTree, SRC, srcId, srcUri} from "/@lib@/wsp/src.js";
import {ESearchItemSrcTypes, ISearchCrit, SearchItemSrcType, SearchLastModif, SearchLastUser, SearchRegexpUri, SearchRequest} from "/@lib@/wsp/search.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {EFolderState, GridColTreeDef, GridDataHolderJsonTree, IGridCategorizer} from "/@back@/commons/widgets/tree.js";
import {GridColDef, IGridDataRow} from "/@back@/commons/widgets/grid-core.js";
import {CellBuilderSrcIconCode} from "/@back@/wsp/widgets/srcGridColumns.js";
import {ISrcGridDatas, SrcGrid, SrcGridDatas} from "/@back@/wsp/widgets/srcGrid.js";
import {AccelKeyMgr, IAction} from "/@lib@/commons/actions.js";
import {InputPeriod} from "/@back@/core/widgets/inputPeriod.js";
import {SpaceTreeReqUri} from "/@back@/wsp/views/spaceTree.js";
import {InfoFocusItem, IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {MoveToSrc, SrcAction} from "/@back@/wsp/actions/srcActions.js";
import {AppHeader} from "/@back@/core/widgets/appHeader.js";
import {EItemTypeFamily} from "/@lib@/wsp/wspMetaUi.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {ItemViewerSingle} from "/@back@/wsp/views/itemViewerSingle.js";
import {ESrcRights} from "/@lib@/commons/security.js";
import {WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {Button, OButtonInit} from "/@back@/commons/widgets/buttons.js";
import {InputUserPanel, OInputUserInit} from "/@back@/core/widgets/inputs.js";
import {EUserType} from "/@lib@/core/user.js";

export interface OWspTrashInit extends OSkinableInit {
	reg?: IReg<IWspUiEnv>

	/** Affiche le panel de la mainView. */
	showMainView?: boolean

	/** Node à sélectionner à l'init. */
	selectLiveSrcUri?: srcUri
}

export interface WspTrash extends BaseElementAsync, IView {
	initialize(init: OWspTrashInit): this
}

export class WspTrash extends BaseElementAsync implements IView, IViewsContainer, IGridCategorizer<JSrcFields, JSrcFieldsTree>, IShortDescCtx {

	reg: IReg<IWspUiEnv>;

	maxEntries = 20_000;

	_grid: SrcGrid;

	_treedatas: WspTrashTreeDatas;

	critDirty: boolean = false;
	spaceRoot: srcUri = SRC.URI_ROOT;

	protected _appHeader: AppHeader<WspTrash>;
	protected _spaceRootBtn: Button;
	protected _removedBy: InputUserPanel<any>;
	protected _inputTxt: HTMLInputElement;
	protected _inputPeriod: InputPeriod;
	protected _inputRecursive: HTMLInputElement;

	protected _searchBtn: Button;

	protected _mainView: ItemViewerSingle;

	protected _srcUriToSelOnInit: srcId;

	protected _lastError: any;

	protected _fetching: Promise<JSrcFields[]>;

	get wsp(): Wsp {return this.reg.env.wsp};

	place: WspsLivePlace;

	infoBroker: IInfoBroker;

	get shortDescs(): JSrcFields[] {return this._grid.shortDescs};

	get emitter() {return this};

	setSpaceRoot(root: srcUri) {
		if (!root) root = SRC.URI_ROOT;
		if (this.spaceRoot != root) {
			this.spaceRoot = root;
			this._spaceRootBtn.label = ITEM.extractSpaceLabel(this.spaceRoot);
			this.setCritDirty();
		}
	}

	setCritDirty() {
		if (!this.critDirty) {
			this.critDirty = true;
			this.resetList();
		}
	}

	protected async _initialize(init: OWspTrashInit): Promise<void> {
		this.reg = REG.createSubReg(this.findReg(init));
		this._initReg(init);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this._initAndInstallSkin(this.localName, init);
		this.infoBroker = this.reg.env.infoBroker;
		this._spaceRootBtn = <Button id="spaceRoot" î={{
			reg: this.reg,
			label: ITEM.extractSpaceLabel(SRC.URI_ROOT),
			uiContext: "dialog",
			title: "Sélectionner un espace..."
		} as OButtonInit} onclick={this.onChooseSpace}/> as Button;

		this._removedBy = new InputUserPanel().initialize({
			required: false,
			reg: this.reg,
			emptySelectionMsg: "Choisir un compte...",
			userCard: "multi",
			usersGridInit: {
				usersSrv: this.reg.env.universe.useUsers,
				grid: {selType: "multi"},
				filterType: EUserType.user,
				filterTypeInputVisibility: true,
			},
		} as OInputUserInit<any>);
		await this._removedBy.initializedAsync;

		this._inputTxt = <input id="codeFilter" type="search" spellcheck="false"/> as HTMLInputElement;
		this._inputPeriod = new InputPeriod();
		this._inputPeriod.title = "Période dans laquelle les contenus ont été mis en corbeille";
		this._inputRecursive = <input id="recursive" type="checkbox"/> as HTMLInputElement;

		this._searchBtn = <Button id="search" î={{reg: this.reg, label: "Rechercher", uiContext: "dialog"} as OButtonInit} onclick={this.doSearch.bind(this)}/> as Button;

		const gridDatas = new SrcGridDatas();
		gridDatas.useLiveSrcUri = true;

		this._grid = new WspTrashGrid().initialize({
			reg: this.reg,
			itemHandlingReact: this.infoBroker,
			actions: this.reg.getList("actions:wspTrash:shortDesc"),
			/* defaultAction: infoBroker ? new SpaceTreeFocusItem() : null, */
			accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.reg.getListAsMap("accelkeys:wspTrash:shortDesc")),
			hideHeaders: true,
			skinOver: "wsp-src-grid",
			skinScroll: "scroll/small",
			dataHolder: gridDatas,
			emptyBody: () => {
				if (this.critDirty) return this._searchBtn;
				if (this._fetching || this._waitingExec) return <c-msg label="Recherche dans la corbeille..." level="info"/>;
				if (this._lastError) return <c-msg label="Erreur au chargement" level="error"/>;
				return <c-msg label="Aucun contenu en corbeille trouvé" level="info"/>;
			}
		});

		this._treedatas = new WspTrashTreeDatas(this.wsp, this.wsp.getShortDescFields(), gridDatas);
		this._grid.setCategorizer(this, this._treedatas);
		this._grid.setAttribute("c-resizable", "");

		this._appHeader = sr.appendChild(new AppHeader<WspTrash>().initialize({
			reg: this.reg,
			focusListening: this._grid,
			actionContext: this,
			forceSharedBar: true
		}));

		sr.appendChild(<div id="crit" onchange={this._onCritChange} oninput={this._onCritChange}>
			<label><span>Espace de recherche</span>{this._spaceRootBtn}</label>
			<label><span>Filtre sur le code</span>{this._inputTxt} <span>Supprimé par</span>{this._removedBy}</label>
			<label><span>Date de mise en corbeille</span>{this._inputPeriod} {this._inputRecursive}<span>Recherche dans les espaces supprimés</span></label>
		</div>);

		if (init.selectLiveSrcUri) {
			this._inputTxt.value = init.selectLiveSrcUri;
			this._inputRecursive.checked = true;
			this._srcUriToSelOnInit = init.selectLiveSrcUri;
		}

		this._fetchRootDatas(); //avant appendChild(this._grid); pour init après lancement de la requete -> emptyBody ok.

		if (init.showMainView) {
			const parentMainView = <div id="srcView" c-resizable=""/>;
			sr.appendChild(<div id="main">
				{this._grid}
				<c-resizer c-orient="row"/>
				{parentMainView}
			</div>);
			this._mainView = new ItemViewerSingle();
			await this._mainView.initViewer(this.reg, parentMainView, null, null);
			this.addEventListener('grid-select', this._onGridSelect);
		} else {
			sr.appendChild(this._grid);
		}
	}

	protected _initReg(init: OWspTrashInit) {
		const wspFrom = this.reg.env.wsp;
		const wsp = this.reg.env.wsp = new Wsp(wspFrom.wspServer, wspFrom.code, wspFrom.infoWsp, wspFrom.wspMetaUi);
		this._initWspReg(wsp);
		if (init.showMainView) {
			//InfoBroker local à cette fenetre de dialogue.
			this.reg.env.infoBroker = new InfoBrokerBasic();
		}

		//Suppr de l'action de copier.
		this.reg.addToList("actions:item:burger", "copy", 2, null);
		//Ajout de l'action de restore sur l'item
		this.reg.addToList("actions:item:burger", "restoreTo", 1, new RestoreToAction());
	}

	protected _initWspReg(wsp: Wsp) {
		//Ajout des champs nécessaires à la gestion de la corbeille
		wsp.reg.addToList('srcFields:shortDesc', 'srcLiveUri', 1, 'srcLiveUri');
		wsp.reg.addToList('srcFields:shortDesc', 'srcTrashed', 1, 'srcTrashed');
		if (wsp.wspDefProps.drvAxis) {
			//Pour différencier un contenu erased qui est propre à l'atelier (drvAxisDefCo===null) : il peut alors être supprimé définitivement.
			wsp.reg.addToList('srcFields:shortDesc', 'drvAxisDefCo', 1, 'drvAxisDefCo');
		}
	}

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		return VIEWS.visitNodes(visitor, options, this._mainView);
	}

	visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return VIEWS.visitNodesAsync(visitor, options, this._mainView);
	}

	onViewShown() {
		if (!this.place) {
			this.reg.env.wsp.listenChanges();
			this.place = this.reg.env.place = this.reg.env.wsp.newPlaceWsp();
			this.place.eventsMgr.on("onConnectionLost", () => {this._appHeader.setHeadband("Votre connexion au serveur est interrompue ou instable.", "error")})
			this.place.eventsMgr.on("onConnectionRenewed", () => {this._appHeader.setHeadband(null)});
			this.place.eventsMgr.on("wspLiveStateChange", this.onWspLiveStateChange.bind(this));
		}
		VIEWS.onViewShown(this._mainView);
	}

	onViewHidden(closed?: boolean) {
		if (closed && this.place) {
			this.reg.env.wsp.stopListenChanges();
			this.place.closePlace();
			this.place = null;
		}
		VIEWS.onViewHidden(this._mainView);
	}

	msgOver: MsgOver;

	protected onWspLiveStateChange(wsp: Wsp) {
		if (wsp !== this.wsp) return;
		if (wsp.isAvailable) {
			this._initWspReg(wsp); //surcharge du reg du wsp après son rechargement.
			if (this.msgOver) {
				this.msgOver.removeMsg();
				this.msgOver = null;
			}
			this.resetList();
			this._fetchRootDatas();
		} else if (wsp.isInError) {
			POPUP.findPopupableParent(this).close();
		} else if (!this.msgOver) {
			this.msgOver = new MsgOver().showMsgOver(this).setCustomMsg("Reconfiguration de l'atelier en cours...");
		}
	}

	fetchRootDatas() {
		return this._fetchRootDatas();
	}

	protected async _fetchRootDatas() {
		if (WSPTRASH_DEBUG) console.log("WspTrash.fetchRootDatas:::start");
		// if (this.callbackSearching) this.callbackSearching.onSearching('searchInit'); pas de searching
		this._lastError = undefined;
		try {
			const requ = this._getRequest();
			if (WSPTRASH_DEBUG) console.log("WspTrash.fetchRootDatas:::request", requ.where.toDom());
			this._fetching = WSP.fetchSearchJson<JSrcFields>(this.wsp, this._grid, requ.toXml());
			const datas = await this._fetching; //this.filterNotTrashedRes(await this._fetching);
			this._fetching = null;
			this._lastError = null;
			if (WSPTRASH_DEBUG) console.log("WspTrash.fetchRootDatas:::datas", datas);
			if (datas.length > this.maxEntries) {
				const maxEntries = this.maxEntries;
				POPUP.showNotifInfo(`Plus de ${maxEntries} éléments trouvés en corbeille. Utilisez les critères de recherche pour restreindre la liste.`, this);
			}
			this._lastError = null;
			this._grid.srcGridDatas.setDatas(datas);
			if (this._srcUriToSelOnInit) {
				const srcUri = this._srcUriToSelOnInit;
				this._srcUriToSelOnInit = null;
				const rowKey = this._grid.srcGridDatas.findRowKeyBySrcUri(srcUri);
				if (rowKey) this._grid.setSelectedRows(this._grid.srcGridDatas.getOffset(rowKey));
			}
		} catch (e) {
			this._fetching = null;
			this._lastError = e;
			console.log(e);
			this._grid.srcGridDatas.setDatas([]);
		} finally {
			//if (this.callbackSearching) this.callbackSearching.onSearching('searchEnd');
			if (WSPTRASH_DEBUG) console.log("WspTrash.fetchRootDatas:::end");
		}
	}

	// protected filterNotTrashedRes(datas: JSrcFields[]): JSrcFields[] {
	// 	if (!datas) return [];
	// 	//On masque les items de type Res non trashed mais qui peuvent être retournés car un de ses flux est en corbeille.
	// 	const wspMetaUi = this.wsp.wspMetaUi;
	// 	return datas.filter((src) => !(!src.srcTrashed && wspMetaUi.getItemType(src.itModel).getFamily() === EItemTypeFamily.res));
	// }


	protected doSearch() {
		this.critDirty = false;
		if (this._waitingExec) return;
		this._fetchRootDatas();
	}

	protected resetList() {
		this._lastError = null;
		this._grid.srcGridDatas.setDatas([]);
	}

	protected _getRequest(): SearchRequest {
		const where = new SearchNodesInTrash(this.spaceRoot, -1, this._inputRecursive.checked, null,
			new SearchItemSrcType(ESearchItemSrcTypes.SPACE | ESearchItemSrcTypes.ITEMFOLDER | ESearchItemSrcTypes.ITEMFILE),
			this._removedBy.value?.length ? new SearchLastUser(this._removedBy.value) : undefined,
			this._inputTxt.value ? new SearchRegexpUri(SpaceTreeReqUri.buildSrcUriPattern(this._inputTxt.value)) : undefined,
			SearchLastModif.buildFromInputPeriod(this._inputPeriod)
		);
		return new SearchRequest(this.wsp.getShortDescDef()).setWhere(where).setMax(this.maxEntries + 1);
	}

	protected _onCritChange(this: HTMLElement) {
		DOMSH.findHost<WspTrash>(this).setCritDirty();
	}

	async onChooseSpace(this: Button, ev: Event) {
		const wspTrash = DOMSH.findHost<WspTrash>(this);
		const {SpaceSelector} = await import("/@back@/wsp/dialogs/spaceSelector.js");
		const ct = new SpaceSelector().initialize({
			reg: wspTrash.reg,
			startSel: wspTrash.spaceRoot,
			spaceTree: {
				showRoot: true
			},
			selOnDblClick: true
		});
		const space = await POPUP.showMenuFromEvent<JSrcFields | undefined>(ct, ev, wspTrash).onNextClose();
		if (space) wspTrash.setSpaceRoot(space.srcUri);
	}

	protected _waitingExec: number;
	protected _lastTxt: string;
	protected _lastMoreThan: number | null;
	protected _lastLessThan: number | null;


	protected _onGridSelect(ev: Event) {
		if (this._showTimer) clearTimeout(this._showTimer);
		const count = this._grid.countSelectedRows();
		if (count === 0) {
			this.reg.env.infoBroker.dispatchInfo(new InfoFocusItem(null), this);
		} else if (count === 1) {
			this._showTimer = setTimeout(() => {
				let sd = this._grid.shortDescs[0];
				if (sd) {
					switch (ITEM.getSrcUriType(sd.srcLiveUri || sd.srcUri)) {
					case "space":
						sd = null;
						break;
					case "res":
						sd = this._treedatas.getRowKeyParent(sd);
						while (ITEM.getSrcUriType(sd.srcUri) !== "item") sd = this._treedatas.getRowKeyParent(sd);
					}
					if (sd && (!this._mainView.longDesc || this._mainView.longDesc.srcUri !== sd.srcUri)) {
						this.reg.env.infoBroker.dispatchInfo(new InfoFocusItem(sd.srcUri, sd), this);
					}
				}
			}, 500);
		}
	}

	protected _showTimer: any;


	getId(data: JSrcFields): any {
		return data.srcLiveUri || data.srcUri;
	}

	getAncestorIds(data: JSrcFieldsTree): any[] | null {
		let parent = SRC.extractUriParent(data.srcLiveUri || data.srcUri);
		if (!parent) return null;
		const stack: any[] = [];
		while (parent) {
			stack.push(parent);
			parent = SRC.extractUriParent(parent);
		}
		stack.reverse();
		return stack;
	}

	wrapForTree(data: JSrcFields): JSrcFieldsTree {
		const e = Object.assign(Object.create(null), data) as JSrcFieldsTree;
		//Déclaration des items de type dossier et des espaces en objets dossiers dans l'arbre.
		if (this.wsp.wspMetaUi.getItemType(data.itModel).getFamily() === EItemTypeFamily.res) {
			//On est sur une res, seul l'item est visible (pas le stream, les  props ou les metas).
			if (ITEM.getSrcUriType(data.srcUri) === "res") return null;
		} else if (!e.ch) {
			if (data.srcSt === ESrcSt.folder) {
				//dossier potentiel
				e.ch = null;
			}
		}
		//Hack des srcRi :
		if (ITEM.isTrashUri(data.srcUri)) {
			//Droits pour "Suppression définitive" et "Restaurer vers..."
			e.srcRi = ESrcRights.READS | ESrcRights.remove | ESrcRights.removeChildren | ESrcRights.move;
		} else {
			//Droits pour "Restaurer vers..." d'un children
			e.srcRi = ESrcRights.READS | ESrcRights.removeChildren;
		}
		return e;
	}

	getFolder(id: any): JSrcFieldsTree {
		//Impl pour tests, ne devrait jamais être appelé...
		//Espace ou item racine de type dossier ancêtre d'un élément supprimé, créé à la volée.
		switch (ITEM.getSrcUriType(id)) {
		case "space":
			return {srcUri: id};
		case "item":
		case "res":
			return {srcUri: id, itModel: "sc_undef"};
		}
		return null;
	}

	shouldRemoveEmptyFolder(folder: JSrcFieldsTree): boolean {
		return false;
	}
}

REG.reg.registerSkin('wsp-trash', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#crit {
		border-top: 1px solid var(--border-color);
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		padding: .5em;
	}

  label {
	  display: flex;
	  align-items: center;
	  margin: .2em 0;
  }

  #spaceRoot,
  #codeFilter,
  c-input-period,
  c-input-users-panel,
  #recursive {
	  margin: 0 .5em;
  }

  #spaceRoot {
	  padding-inline-end: 1.2em;
	  background-size: 1em 1em;
	  background-repeat: no-repeat;
	  background-position: right;
	  background-image: url(/@skin@/commons/widgets/dropdown.svg);
	  font-weight: bold;
  }

  input {
	  padding: 2px;
	  background: var(--form-bgcolor);
	  color: var(--form-color);
	  border: 1px solid var(--border-color);
	  display: flex;
	  flex: 1;
	  font-size: inherit;
  }

  input:focus {
	  outline: var(--focus-outline);
  }

  input::placeholder {
	  color: var(--fade-color);
	  letter-spacing: 2px;
	  font-size: .8em;
	  font-style: italic;
  }

  input:focus::placeholder {
	  color: transparent;
  }

  c-input-period {
	  border: 1px solid var(--border-color);
	  border-radius: 3px;
	}

	#recursive {
		flex: 0;
	}

	#main {
		flex: 1 1 20em;
		display: flex;
		min-height: 0;
		min-width: 0;
		border-top: 1px solid var(--border-color);
	}

	wsp-trash-grid {
		flex: 1;
		border: none;
		background-color: var(--row-bgcolor);
	}

	#srcView {
		flex: 1 1 50%;
		display: flex;
		min-height: 0;
		min-width: 0;
		border-inline-start: 1px solid var(--border-color);
	}
`);


customElements.define("wsp-trash", WspTrash);


/**
 *
 */
class WspTrashGrid extends SrcGrid {

	dataHolder: WspTrashTreeDatas;

	get wspTrash(): WspTrash {return DOMSH.findHost<WspTrash>(this)}

	getDefaultColumnDefs(): GridColDef[] {
		return [new GridColTreeDef('srcTree')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setMinWidth('55px').setSortable(true)
			.setCellBuilder(new CellBuilderSrcIconCode(this.reg, this.wsp.wspMetaUi, false, this.wsp.srcUriItemsSortFn))];
	}

	redrawLine(row: IGridDataRow, line: HTMLElement) {
		super.redrawLine(row, line);
		line.classList.toggle('trashed', row.getData<ESrcField>("srcTrashed") === true);
	}

	isParentInTrash(child: JSrcFields): boolean {
		const parent = this.dataHolder.getRowKeyParent(child);
		return parent ? ITEM.isTrashUri(parent.srcUri) : false;
	}

}

REG.reg.registerSkin('wsp-trash-grid', 1, /* language=CSS */ `
	.trashed {
		text-decoration-line: line-through;
		text-decoration-color: var(--fade-color);
	}
`);

customElements.define("wsp-trash-grid", WspTrashGrid);


class WspTrashTreeDatas extends GridDataHolderJsonTree<JSrcFieldsTree> {
	constructor(readonly wsp: Wsp, readonly srcFields: ESrcField[], readonly srcGridDatas: ISrcGridDatas) {
		super('ch');
		this.setDefaultOpenState((d: JSrcFieldsTree) => d.ch != null);
	}

	async openFolderAsync(rowKey: any): Promise<boolean> {
		if (this.ch(rowKey) === null) {
			//Dossier à charger...
			try {
				if (WSPTRASH_DEBUG) console.log("WspTrashTreeDatas.openFolderAsync:::start", rowKey.srcUri);
				if (this.folderSt(rowKey) !== EFolderState.closed) return false;
				this.setFolderSt(rowKey, EFolderState.opening);
				const datas = await WSP.fetchSearchJson<JSrcFields>(this.wsp, this._grid, new SearchRequest().addFieldColumns(...this.srcFields).setWhere(new SearchNodesInTrash(rowKey.srcUri, -1)).toXml());
				if (!this.isRowkeyAlive(rowKey)) return false; //modification de l'arbre entre temps, noeud perdu.
				//if (this.srcFilter) datas = datas.filter(this.srcFilter);
				this.setFolderSt(rowKey, EFolderState.closed);
				if (WSPTRASH_DEBUG) console.log("WspTrashTreeDatas.openFolderAsync::::datas:", datas);
				if (datas.length > 0) {
					this.srcGridDatas.updateDatas(this.srcGridDatas.getDatas().length, 0, ...datas);
				} else {
					this.setCh(rowKey, datas);
				}
				return this.openFolder(rowKey);
			} catch (e) {
				POPUP.showNotifError("Accès au serveur impossible", this.grid);
				this.closeFolder(rowKey);
				throw e;
			} finally {
				if (WSPTRASH_DEBUG) console.log("WspTrashTreeDatas.openFolderAsync:::end", rowKey.srcUri);
			}
			return false;
		}
		return this.openFolder(rowKey);
	}

}


class SearchNodesInTrash implements ISearchCrit {

	filters: ISearchCrit[];

	constructor(public path?: string, public depth?: -1 | 1, public recursiveTrash?: boolean, public afterUri?: string, ...filters: ISearchCrit[]) {
		this.filters = filters;
	}

	toDom(): Element | undefined {
		return <exp type="NodesInTrash" path={this.path} depth={this.depth} recursiveTrash={this.recursiveTrash} afterUri={this.afterUri}>
			{this.filters ? this.filters.map((f) => f ? f.toDom() : undefined) : undefined}
		</exp>;
	}
}


class RestoreAction extends SrcAction<WspTrashGrid> {
	constructor() {
		super('restore');
		this._label = "Restaurer";
		this._group = "edit";
		this.atLeastOne = true;
		this.isNotRemoved = false;
		this._enableSrcPerms = ["restore.history"];
		this._icon = "/@skin@/wsp/actions/wspRestore.svg";
	}

	isEnabled(ctx: WspTrashGrid): boolean {
		if (ctx.shortDescs.some((s) => !s.srcTrashed || ctx.isParentInTrash(s))) return false; //
		return super.isEnabled(ctx);
	}

	async execute(ctx: WspTrashGrid, ev?: Event) {
		const uris = ctx.shortDescs.map((s) => s.srcUri);
		try {
			await WSP.srcRestore(ctx.wsp, ctx, uris);
		} catch (e) {
			ERROR.report("Une erreur est survenue durant la restauration", e);
		}
	}
}

class RestoreToAction extends MoveToSrc<WspTrashGrid> {
	constructor() {
		super('restoreTo');
		this._label = "Restaurer vers...";
		this._titlePopup = "Restaurer vers...";
		this._moveButtonLabel = "Restaurer";
		this.isNotRemoved = false;
		this._enableSrcPerms = ["restore.history", "write.node.moveFrom"]; //XXX perm write.node.moveFrom à hériter ?
		this._icon = "/@skin@/wsp/actions/wspRestoreTo.svg";
	}

	isVisible(ctx: WspTrashGrid): boolean {
		//On masque si un des élements sélectionnés :
		// - n'est pas en corbeille (cela n'a pas de sens)
		// - est une res : TODO il faudrait développer un sélecteur de dossier de res, à la place d'un sélecteur d'espace.
		if (ctx.shortDescs.some((s) => !ITEM.isTrashUri(s.srcUri) || ITEM.getSrcUriType(s.srcUri) === "res")) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: WspTrashGrid): boolean {
		return super.isEnabled(ctx);
	}

	async execute(ctx: WspTrashGrid, ev?: Event) {
		if (ctx.shortDescs.length === 1) {
			this.targetFolder = SRC.extractUriParent(ctx.shortDescs[0].srcLiveUri);
		}
		const srcUris = await super.execute(ctx, ev);
		if (!srcUris) return null;
		const srcsToRestore = (await WSP.fetchSrcs(ctx.wsp, ctx, srcUris, ["srcUri", "srcSt"])).filter((s) => s.srcSt === ESrcSt.none);
		if (srcsToRestore.length > 0) await WSP.srcRestore(ctx.wsp, ctx, srcsToRestore.map((s) => s.srcUri));
		await ctx.wspTrash.fetchRootDatas();
		return srcUris;
	}
}

class DeletePermAction extends SrcAction<IShortDescCtx> {
	constructor() {
		super('deletePerm');
		this._label = "Supprimer définitivement";
		this._group = "del";
		this.atLeastOne = true;
		this._enableSrcPerms = ["deletePermanently.trash"];
		this._icon = "/@skin@/wsp/actions/wspDeletePermanently.svg";
	}

	isEnabled(ctx: WspTrashGrid): boolean {
		const wspDefProps = ctx.reg.env.wsp.wspDefProps;
		if (ctx.shortDescs.some((sd) => {
			if (this.isSrcLayered(wspDefProps, sd)) return true;
			return !ITEM.isTrashUri(sd.srcUri)
		})) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: WspTrashGrid, ev?: Event) {
		const count = ctx.shortDescs.length;
		if (await POPUP.confirm(count === 1 ? "Confirmez la suppression définitive." : `Confirmez la suppression définitive de ces ${count} éléments.`,
			ctx, {okLbl: "Supprimer définitivement"})) {
			const uris = ctx.shortDescs.map((s) => s.srcUri);
			try {
				await WSP.srcDeletePermanently(ctx.wsp, ctx, uris);
			} catch (e) {
				ERROR.report("Une erreur est survenue durant la suppression définitive.", e);
			}
		}
	}
}


function addAction(action: IAction<IShortDescCtx>, accel?: string, sortKey?: number) {
	REG.reg.addToList("actions:wspTrash:shortDesc", action.getId(), 1, action, sortKey);
	if (accel) REG.reg.addToList("accelkeys:wspTrash:shortDesc", accel, 1, action);
}

addAction(new RestoreAction());
addAction(new RestoreToAction());
addAction(new DeletePermAction());


const WSPTRASH_DEBUG = false;
